import { csiAPI } from "src/boot/axios"
import { CSIurl } from "src/util/enums/urls"
import { concatUrl, getCustomConfig, getNewAbortHandler } from "src/util/http/httpHelper"
import { ObjectList, PayloadEntity } from "src/util/types/dataModels/genericObject"
import { HttpConfig } from "src/util/types/dataModels/http"
import { ItemClass } from "src/util/types/dataModels/itemClass"
import {
  PERMISSION_ACCESS_CREATE,
  PERMISSION_ACCESS_READ,
  PERMISSION_ACCESS_UPDATE,
  PERMISSION_ITEMS,
} from "src/util/types/dataModels/user"

/**
 * Get the list of all existing Item Classes
 *
 * @param config custom configurations and filtering params
 */
export async function getItemClassesList(config: HttpConfig = {}): Promise<ObjectList<ItemClass>> {
  const entityURL = CSIurl.ITEM_CLASSES_LIST
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Create a new Item Class
 *
 * @param config custom configurations and filtering params
 * @param itemClass the new Item Class
 */
export async function postItemClass(
  config: HttpConfig = {},
  itemClass: PayloadEntity<ItemClass>
): Promise<ItemClass> {
  const url = CSIurl.ITEM_CLASSES_LIST
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, itemClass, { signal, customConfig })

  return data
}

/**
 * Get an individual Item Class
 *
 * @param config custom configurations and filtering params
 * @param itemClassId
 */
export async function getItemClass(
  config: HttpConfig = {},
  itemClassId: string
): Promise<ItemClass> {
  const url = CSIurl.ITEM_CLASSES_ID.replace("{id}", itemClassId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Replace an existing individual Item Class
 *
 * @param config custom configurations and filtering params
 * @param itemClassId the id of the Item Class that should be updated
 * @param itemClass the updated Item Class
 */
export async function putItemClass(
  config: HttpConfig = {},
  itemClassId: string,
  itemClass: PayloadEntity<ItemClass>
): Promise<ItemClass> {
  const url = CSIurl.ITEM_CLASSES_ID.replace("{id}", itemClassId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, itemClass, { signal, customConfig })

  return data
}

/**
 * Partially update an individual Item Class
 *
 * @param config custom configurations and filtering params
 * @param itemClassId the id of the Item Class that should be partially updated
 * @param itemClass the updated Item Class
 */
export async function patchItemClass(
  config: HttpConfig = {},
  itemClassId: string,
  itemClass: Partial<PayloadEntity<ItemClass>>
): Promise<ItemClass> {
  const url = CSIurl.ITEM_CLASSES_ID.replace("{id}", itemClassId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, itemClass, { signal, customConfig })

  return data
}
